<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";

import { api, ApiError } from "@/api/client";
import type { AuditEvent, AuditQuery, AuditSeverity } from "@/api/types";

const { t } = useI18n();

const events = ref<AuditEvent[]>([]);
const loading = ref(true);
const listError = ref<string | null>(null);
const nextCursor = ref<number | null>(null);

const filters = reactive<AuditQuery>({
  action: undefined,
  severity: undefined,
  operator_id: undefined,
  target_type: undefined,
  target_id: undefined,
  from: undefined,
  to: undefined,
  limit: 50,
  cursor: undefined,
});

const SEVERITIES: AuditSeverity[] = ["info", "warn", "security", "critical"];

async function load(append = false) {
  loading.value = true;
  listError.value = null;
  try {
    const resp = await api.admin.audit.list({
      ...filters,
      cursor: append ? (nextCursor.value ?? undefined) : undefined,
    });
    events.value = append ? [...events.value, ...resp.events] : resp.events;
    nextCursor.value = resp.next_cursor;
  } catch (err) {
    if (err instanceof ApiError) {
      listError.value = `${err.code} (HTTP ${err.status})`;
    } else if (err instanceof Error) {
      listError.value = err.message;
    } else {
      listError.value = "unknown error";
    }
  } finally {
    loading.value = false;
  }
}

function applyFilters() {
  nextCursor.value = null;
  void load(false);
}

function clearFilters() {
  filters.action = undefined;
  filters.severity = undefined;
  filters.operator_id = undefined;
  filters.target_type = undefined;
  filters.target_id = undefined;
  filters.from = undefined;
  filters.to = undefined;
  void load(false);
}

onMounted(() => load(false));

function severityTone(s: AuditSeverity): string {
  switch (s) {
    case "info":
      return "info";
    case "warn":
      return "warn";
    case "security":
    case "critical":
      return "bad";
  }
}

function formatTime(ts: string): string {
  // Compact RFC3339 → local time string for the table cell.
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toISOString().replace("T", " ").slice(0, 19);
}

function formatDetails(d: unknown): string {
  if (d === null || d === undefined) return "";
  if (typeof d === "string") return d;
  try {
    return JSON.stringify(d);
  } catch {
    return String(d);
  }
}
</script>

<template>
  <div class="admin-page">
    <div class="admin-page__header">
      <h1>{{ t("admin.nav.audit") }}</h1>
    </div>

    <section class="admin-card">
      <form class="admin-form" @submit.prevent="applyFilters">
        <label>
          <span>{{ t("admin.audit.filter_action") }}</span>
          <input v-model="filters.action" :placeholder="t('admin.audit.action_placeholder')" />
        </label>
        <label>
          <span>{{ t("admin.audit.filter_severity") }}</span>
          <select v-model="filters.severity">
            <option :value="undefined">{{ t("admin.audit.any") }}</option>
            <option v-for="s in SEVERITIES" :key="s" :value="s">{{ s }}</option>
          </select>
        </label>
        <label>
          <span>{{ t("admin.audit.filter_operator_id") }}</span>
          <input v-model="filters.operator_id" />
        </label>
        <label>
          <span>{{ t("admin.audit.filter_target_type") }}</span>
          <input v-model="filters.target_type" />
        </label>
        <label>
          <span>{{ t("admin.audit.filter_from") }}</span>
          <input v-model="filters.from" :placeholder="'2026-05-01T00:00:00Z'" />
        </label>
        <label>
          <span>{{ t("admin.audit.filter_to") }}</span>
          <input v-model="filters.to" :placeholder="'2026-06-01T00:00:00Z'" />
        </label>
        <button type="submit" class="admin-form__submit">{{ t("admin.audit.apply") }}</button>
        <button type="button" @click="clearFilters">{{ t("admin.audit.clear") }}</button>
      </form>
    </section>

    <section class="admin-card">
      <p v-if="loading && events.length === 0" class="admin-empty">{{ t("admin.loading") }}</p>
      <p v-else-if="listError" class="admin-error">{{ listError }}</p>
      <p v-else-if="events.length === 0" class="admin-empty">{{ t("admin.no_results") }}</p>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>{{ t("admin.audit.col_time") }}</th>
            <th>{{ t("admin.audit.col_action") }}</th>
            <th>{{ t("admin.audit.col_severity") }}</th>
            <th>{{ t("admin.audit.col_target") }}</th>
            <th>{{ t("admin.audit.col_operator") }}</th>
            <th>{{ t("admin.audit.col_details") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ev in events" :key="ev.id">
            <td class="admin-table__id" :data-label="t('admin.audit.col_time')">
              {{ formatTime(ev.created_at) }}
            </td>
            <td :data-label="t('admin.audit.col_action')">{{ ev.action }}</td>
            <td :data-label="t('admin.audit.col_severity')">
              <span class="admin-badge" :data-tone="severityTone(ev.severity)">{{
                ev.severity
              }}</span>
            </td>
            <td class="admin-table__id" :data-label="t('admin.audit.col_target')">
              <template v-if="ev.target_type">
                {{ ev.target_type }}/{{ ev.target_id?.slice(0, 8) }}
              </template>
              <template v-else>—</template>
            </td>
            <td class="admin-table__id" :data-label="t('admin.audit.col_operator')">
              {{ ev.operator_id?.slice(0, 8) ?? "—" }}
            </td>
            <td class="admin-table__id" :data-label="t('admin.audit.col_details')">
              <span class="audit__details" :title="formatDetails(ev.details)">{{
                formatDetails(ev.details)
              }}</span>
            </td>
          </tr>
        </tbody>
      </table>
      <div
        v-if="nextCursor !== null"
        style="margin-top: 0.75rem; display: flex; justify-content: center"
      >
        <button type="button" :disabled="loading" @click="load(true)">
          {{ t("admin.audit.load_more") }}
        </button>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* Details can be a long JSON blob — clamp it to one line with an ellipsis and
   expose the full value on hover (title). On the mobile card layout the cell is
   already width-constrained, so the same clamp keeps rows tidy. */
.audit__details {
  display: inline-block;
  max-width: 32ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
